/**
 * Telegram Mini App Integration
 * https://core.telegram.org/bots/webapps
 */

// Check if running inside Telegram
export const isTelegramWebApp = () => {
  return window.Telegram?.WebApp !== undefined;
};

// Get Telegram WebApp instance
export const getTelegramWebApp = () => {
  return window.Telegram?.WebApp;
};

// Initialize Telegram Mini App
export const initTelegramApp = () => {
  const tg = getTelegramWebApp();
  if (!tg) return null;

  // Expand to full height
  tg.expand();
  // Prevent swipe-down from collapsing/scrolling the app while dragging pieces
  tg.disableVerticalSwipes?.();

  // Enable closing confirmation
  tg.enableClosingConfirmation();

  // Set header color to match app theme
  tg.setHeaderColor('#09090b');
  tg.setBackgroundColor('#09090b');

  // Ready to show
  tg.ready();

  return tg;
};

// Get user data from Telegram
export const getTelegramUser = () => {
  const tg = getTelegramWebApp();
  if (!tg) return null;

  return tg.initDataUnsafe?.user || null;
};

// Get init data for authentication
export const getTelegramInitData = () => {
  const tg = getTelegramWebApp();
  if (!tg) return null;

  return tg.initData;
};

// Get platform info
export const getTelegramPlatform = () => {
  const tg = getTelegramWebApp();
  if (!tg) return 'unknown';

  return tg.platform;
};

// Get color scheme (dark/light)
export const getTelegramColorScheme = () => {
  const tg = getTelegramWebApp();
  if (!tg) return 'dark';

  return tg.colorScheme;
};

// Show main button
export const showMainButton = (text, onClick) => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.MainButton.text = text;
  tg.MainButton.onClick(onClick);
  tg.MainButton.show();
};

// Hide main button
export const hideMainButton = () => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.MainButton.hide();
};

// Show back button
export const showBackButton = (onClick) => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.BackButton.onClick(onClick);
  tg.BackButton.show();
};

// Hide back button
export const hideBackButton = () => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.BackButton.hide();
};

// Haptic feedback
export const hapticFeedback = (type = 'impact') => {
  const tg = getTelegramWebApp();
  if (!tg?.HapticFeedback) return;

  switch (type) {
    case 'impact':
      tg.HapticFeedback.impactOccurred('medium');
      break;
    case 'notification':
      tg.HapticFeedback.notificationOccurred('success');
      break;
    case 'selection':
      tg.HapticFeedback.selectionChanged();
      break;
    default:
      tg.HapticFeedback.impactOccurred('light');
  }
};

// Show alert
export const showTelegramAlert = (message) => {
  const tg = getTelegramWebApp();
  if (!tg) {
    alert(message);
    return;
  }

  tg.showAlert(message);
};

// Show confirm
export const showTelegramConfirm = (message, callback) => {
  const tg = getTelegramWebApp();
  if (!tg) {
    const result = confirm(message);
    callback(result);
    return;
  }

  tg.showConfirm(message, callback);
};

// Close Mini App
export const closeTelegramApp = () => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.close();
};

// Open link in Telegram browser
export const openTelegramLink = (url) => {
  const tg = getTelegramWebApp();
  if (!tg) {
    window.open(url, '_blank');
    return;
  }

  tg.openLink(url);
};

// Open Telegram link (t.me)
export const openTelegramInternalLink = (url) => {
  const tg = getTelegramWebApp();
  if (!tg) {
    window.open(url, '_blank');
    return;
  }

  tg.openTelegramLink(url);
};

// Request contact
export const requestContact = (callback) => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.requestContact(callback);
};

// Is Telegram Premium user
export const isTelegramPremium = () => {
  const user = getTelegramUser();
  return user?.is_premium || false;
};

// Get theme params
export const getTelegramThemeParams = () => {
  const tg = getTelegramWebApp();
  if (!tg) return {};

  return tg.themeParams || {};
};

export default {
  isTelegramWebApp,
  getTelegramWebApp,
  initTelegramApp,
  getTelegramUser,
  getTelegramInitData,
  getTelegramPlatform,
  getTelegramColorScheme,
  showMainButton,
  hideMainButton,
  showBackButton,
  hideBackButton,
  hapticFeedback,
  showTelegramAlert,
  showTelegramConfirm,
  closeTelegramApp,
  openTelegramLink,
  isTelegramPremium,
  getTelegramThemeParams
};
